import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertCircle, KeyRound, Copy, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuthModal } from "../context/AuthModalContext";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";

const inputCls = "w-full h-11 px-3.5 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 transition-colors";

const NewPassword = ({ password, onDone }) => (
  <div className="space-y-4" data-testid="forgot-result">
    <div className="flex gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
      <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> Your password was reset and you're logged in. Save this new password:
    </div>
    <div className="flex items-center gap-2">
      <code className="flex-1 h-11 leading-[44px] px-3.5 rounded-lg bg-slate-900 text-white font-mono text-sm tracking-wide" data-testid="forgot-new-password">{password}</code>
      <button type="button" onClick={() => { Promise.resolve(navigator.clipboard?.writeText(password)).then(() => toast.success("Copied")).catch(() => toast.error("Couldn't copy — please copy it manually")); }} className="h-11 w-11 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 flex items-center justify-center" data-testid="forgot-copy-btn" aria-label="Copy password">
        <Copy size={15} />
      </button>
    </div>
    <button type="button" onClick={onDone} className="w-full h-11 rounded-lg bg-slate-900 text-white font-semibold text-sm hover:bg-slate-700" data-testid="forgot-continue-btn">Continue</button>
  </div>
);

const ForgotPasswordModal = () => {
  const { mode, close, switchMode } = useAuthModal();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [made, setMade] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const open = mode === "forgot";
  const reset = () => { setEmail(""); setPw(""); setMade(""); setError(""); };
  const done = () => { reset(); close(); navigate("/home"); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.includes("@")) return setError("Please enter a valid email");
    if (pw && pw.length < 6) return setError("New password must be at least 6 characters");
    setBusy(true);
    try {
      const r = await resetPassword({ email: email.trim(), new_password: pw || null });
      if (r.new_password) return setMade(r.new_password);
      toast.success("Password updated — you're logged in");
      done();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { if (made) return done(); reset(); close(); } }}>
      <DialogContent className="sm:max-w-[420px] bg-white border-slate-200" data-testid="forgot-modal">
        <DialogTitle className="font-display text-2xl font-bold text-slate-900">Reset your password</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">
          {made ? "All done." : "Type your email and a new password. Leave the password empty and we'll make one for you."}
        </DialogDescription>
        {made ? <NewPassword password={made} onDone={done} /> : (
          <form onSubmit={submit} noValidate className="space-y-3">
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600" data-testid="forgot-error">
                <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
              </div>
            )}
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoFocus autoComplete="email" data-testid="forgot-email-input" className={inputCls} />
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (optional)" autoComplete="new-password" data-testid="forgot-password-input" className={inputCls} />
            <button type="submit" disabled={busy} data-testid="forgot-submit-btn" className="w-full h-11 rounded-lg bg-slate-900 text-white font-semibold text-sm hover:bg-slate-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Reset password
            </button>
            <button type="button" onClick={() => { reset(); switchMode("login"); }} className="w-full text-xs text-slate-500 hover:text-slate-900" data-testid="forgot-back-login">Back to login</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
